/**
 * Run-hang beacon document schema + path. Written append-only by the webapp
 * when a `runCode` call in `@cip/code-runner` doesn't reach `FINISHED` within
 * the watchdog window. Admins read via {@link getRunHangReportsCollectionRef}
 * in the client module.
 *
 * `ClientRunHangReportSchema` is the client-prepared portion (everything except
 * `createdAt`). The Firestore converter stamps `createdAt` via
 * `serverTimestamp()` at write time so beacons carry server-authoritative time.
 */
import {z} from 'zod';
import {FirestoreTimestampSchema} from '../types';

/** Lifecycle checkpoint values; mirrors `RunCheckpoint` in `@cip/code-runner`. */
export const RunCheckpointSchema = z.enum([
  'ENQUEUED',
  'API_REG_AWAITING',
  'API_REG_DONE',
  'AUTH_TOKEN_AWAITING',
  'AUTH_TOKEN_DONE',
  'RUN_POSTED',
  'STDIN_REQUESTED',
  'STDIN_RESPONDED',
  'FINISHED',
]);

/** Inferred type for {@link RunCheckpointSchema}. */
export type RunCheckpointValue = z.infer<typeof RunCheckpointSchema>;

/**
 * Client-prepared portion of a hang beacon — everything except `createdAt`,
 * which the Firestore converter stamps on write. `.loose()` so future fields
 * don't break readers.
 */
export const ClientRunHangReportSchema = z
  .object({
    /** Signed-in user, or `null` for anonymous viewers. */
    userId: z.string().nullable(),
    /** Opaque per-run id from `@cip/code-runner`. */
    runId: z.string(),
    /** Run lifecycle checkpoint reached most recently. */
    lastCheckpoint: RunCheckpointSchema,
    /** Milliseconds since `lastCheckpoint` was entered, at firing time. */
    msStuckAtCheckpoint: z.number(),
    /** Worker has asked main for stdin and main hasn't replied. */
    awaitingStdin: z.boolean(),
    /** Sync-channel message id of the pending stdin reply, or `null`. */
    pendingStdinMessageId: z.string().nullable(),
    /** Final checkpoint of the prior run on this plug, or `null`. */
    previousRunOutcome: RunCheckpointSchema.nullable(),
    /** `testMode` option for this `runCode` call. */
    testMode: z.unknown(),
    /** `stepMode` option for this `runCode` call. */
    stepMode: z.boolean(),
    /** 0 for the initial fire; increments per follow-up. */
    firingIndex: z.number(),
    /** `navigator.serviceWorker.controller?.scriptURL`; flags stale SW carryover. */
    serviceWorkerControllerScriptUrl: z.string().nullable(),
    /** Whether `SharedArrayBuffer` was available at firing time. */
    sharedArrayBufferAvailable: z.boolean(),
    /** `navigator.userAgent` at firing time. */
    userAgent: z.string(),
    /** Full `window.location.href`; dashboard parses course/assn/project from this. */
    url: z.string(),
    /** Build SHA from the Vite `define` plugin; correlates beacons with deploys. */
    commit: z.string(),
    /** Stable per-tab session id from `getOrCreateSessionId()`. */
    sessionId: z.string(),
  })
  .loose();

/** Inferred type for {@link ClientRunHangReportSchema}. */
export type ClientRunHangReport = z.infer<typeof ClientRunHangReportSchema>;

/**
 * Full stored shape: client payload plus server-stamped `createdAt`. SDK-agnostic
 * (uses {@link FirestoreTimestampSchema}). Client/admin modules extend
 * {@link ClientRunHangReportSchema} with their own timestamp transforms.
 */
export const RunHangReportDocumentSchema = ClientRunHangReportSchema.extend({
  /** Server-assigned write time (set by the client converter via `serverTimestamp()`). */
  createdAt: FirestoreTimestampSchema,
});

/** Inferred type for {@link RunHangReportDocumentSchema}. */
export type RunHangReportDocument = z.infer<typeof RunHangReportDocumentSchema>;

/** Top-level Firestore collection. */
export const RUN_HANG_REPORTS_COLLECTION = 'runHangReports' as const;

/** Returns the Firestore collection path `runHangReports`. */
export function getRunHangReportsCollectionPath(): string {
  return RUN_HANG_REPORTS_COLLECTION;
}
